import { useContext } from 'react'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import {
  digObject, generateID, stringToKey, toggleArray,
} from '@domain-group/javascript-utils'

import { useForm, UseFormPayload } from '@domain-group/react-hooks'

import useSystem from '@hooks/useSystem'

import defaultFormState, { requiredFields } from '@models/templateQualityControlCategory'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

const MODAL_KEY = 'CreateOrEditTemplateQualityControlCategoryModal'

type ModalCallbacks = {
  closeModal: () => void,
  showModal: boolean,
  updateSystem: (payload: HandleCallbackActionParams) => Promise<object>,
}

type CreateOrEditCategoryProps = {
  callbacks: ModalCallbacks,
  templateQualityControlCategories: [],
  isEditing: boolean,
  options: object,
  entityState: UseFormPayload['entityState'],
  updateFn: Function,
}

const createOrEditCategory = (params: CreateOrEditCategoryProps) => {
  const {
    callbacks: { closeModal, updateSystem },
    templateQualityControlCategories,
    entityState,
    isEditing,
    options,
    updateFn,
  } = params

  const updatedCategories = toggleArray(templateQualityControlCategories, entityState, { useObjectKey: 'id' })

  const updatedSystem = {
    options: {
      ...options,
      template_qc_categories: updatedCategories,
    },
  }

  const actionText = isEditing ? 'Updated' : 'Created'

  const payload: HandleCallbackActionParams = {
    callbacks: {
      // @ts-expect-error TS2322
      action: updateFn,
      afterAction: closeModal,
    },
    entityParams: updatedSystem,
    toastText: `Template QC Category ${actionText}`,
  }

  updateSystem(payload)
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditTemplateQualityControlCategoryModal = (props: ModalProps) => {
  const { callbacks, showModal } = props

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { category, system: initSystem } = modalPayload

  const initCategory = category || { id: generateID() }

  const {
    callbacks: { updateSystem: updateFn },
    system: { options },
    updating,
  } = useSystem(initSystem)

  const templateQualityControlCategories = digObject(options, 'template_qc_categories', [])

  const formPayload = useForm(
    defaultFormState,
    { entity: initCategory, requiredFields, validateOn: 'change' },
    [category?.id],
  )

  const {
    entityState,
    entityState: { title, key },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = formPayload

  const isEditing = category?.id
  const updateOrCreateButtonText = isEditing ? 'Save Category' : 'Create Category'

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title={isEditing ? 'Edit' : 'Add'} titleSecondLine="Category" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Title" errorMessage={errors.title}>
            <input
              name="title"
              value={title}
              onBlur={e => setEntityState({ key: stringToKey(e.target.value) })}
              onChange={e => setEntityState({ title: e.target.value })}
            />
          </Form.Field>

          <Form.Field label="* Key" marginTop="large" errorMessage={errors.value}>
            <input name="key" value={key} {...handlers} />
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={updating}
          onClick={() => createOrEditCategory({
            callbacks,
            templateQualityControlCategories,
            entityState,
            isEditing,
            options,
            updateFn,
          })}
          size="large"
        >
          {updateOrCreateButtonText}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditTemplateQualityControlCategoryModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
